import { reactQueryGet } from "@circle-react/helpers/backendRequestHelpers";
import { internalApi } from "@circle-react/helpers/urlHelpers/internalApi";
import { RECORD_TYPES } from "../types";
import type {
  AddAction,
  ApiResponse,
  CommunityMember,
  InitialFetchFailureAction,
  InitialFetchStartAction,
  InitialFetchSuccessAction,
  MemberTag,
  RecordType,
  RemoveAction,
  ResetAction,
  SelectionAction,
  Space,
  SpaceGroup,
  Value,
} from "../types";

export const SELECTION_ACTIONS = {
  ADD: "ADD",
  REMOVE: "REMOVE",
  INITIAL_FETCH_START: "INITIAL_FETCH_START",
  INITIAL_FETCH_SUCCESS: "INITIAL_FETCH_SUCCESS",
  INITIAL_FETCH_FAILURE: "INITIAL_FETCH_FAILURE",
  RESET: "RESET",
} as const;

export const add = (
  record: RecordType,
  includeAllResultTypes: boolean,
  shouldUpdateValue: boolean,
): AddAction => ({
  type: SELECTION_ACTIONS.ADD,
  payload: { record, includeAllResultTypes, shouldUpdateValue },
});

export const remove = (
  record: RecordType,
  includeAllResultTypes: boolean,
): RemoveAction => ({
  type: SELECTION_ACTIONS.REMOVE,
  payload: { record, includeAllResultTypes },
});

export const reset = (includeAllResultTypes: boolean): ResetAction => ({
  type: SELECTION_ACTIONS.RESET,
  payload: { includeAllResultTypes },
});

const fetchSpaceGroups =
  (spaceGroupIds: number[] = [], includeAllResultTypes: boolean) =>
  (dispatch: (action: SelectionAction) => void): Promise<void> => {
    if (spaceGroupIds?.length) {
      return reactQueryGet<ApiResponse<SpaceGroup>>(
        internalApi.spaceGroups.index(),
      )
        .then(response => {
          response?.records
            ?.filter(record => spaceGroupIds.includes(record.id))
            ?.forEach(record => {
              dispatch(
                add(
                  { ...record, type: RECORD_TYPES.SPACE_GROUP },
                  includeAllResultTypes,
                  false,
                ),
              );
            });
        })
        .catch(console.error);
    }
    return Promise.resolve();
  };

const fetchSpaces =
  (spaceIds: number[] = [], includeAllResultTypes: boolean) =>
  (dispatch: (action: SelectionAction) => void): Promise<void> => {
    if (spaceIds?.length) {
      return reactQueryGet<ApiResponse<Space>>(internalApi.spaces.index())
        .then(response => {
          response?.records
            ?.filter(record => spaceIds.includes(record.id))
            ?.forEach(record => {
              dispatch(
                add(
                  { ...record, type: RECORD_TYPES.SPACE },
                  includeAllResultTypes,
                  false,
                ),
              );
            });
        })
        .catch(console.error);
    }
    return Promise.resolve();
  };

const fetchMemberTags =
  (memberTagIds: number[] = [], includeAllResultTypes: boolean) =>
  (dispatch: (action: SelectionAction) => void): Promise<void> => {
    if (memberTagIds?.length) {
      return reactQueryGet<ApiResponse<MemberTag>>(
        internalApi.memberTags.index({
          params: {},
        }),
      )
        .then(response => {
          response?.records
            ?.filter(record => memberTagIds.includes(record.id))
            ?.forEach(record => {
              dispatch(
                add(
                  { ...record, type: RECORD_TYPES.MEMBER_TAG },
                  includeAllResultTypes,
                  false,
                ),
              );
            });
        })
        .catch(console.error);
    }
    return Promise.resolve();
  };

const fetchCommunityMembers =
  (communityMemberIds: number[] = [], includeAllResultTypes: boolean) =>
  (dispatch: (action: SelectionAction) => void): Promise<void> => {
    if (communityMemberIds?.length) {
      return reactQueryGet<ApiResponse<CommunityMember>>(
        internalApi.search.communityMembers.index({
          params: {
            filters: {
              id: communityMemberIds,
            },
          },
        }),
      )
        .then(response => {
          response?.records?.forEach(record => {
            dispatch(
              add(
                { ...record, type: RECORD_TYPES.COMMUNITY_MEMBER },
                includeAllResultTypes,
                false,
              ),
            );
          });
        })
        .catch(console.error);
    }
    return Promise.resolve();
  };

export const fetchInitialRecords =
  (value: Value, includeAllResultTypes: boolean) =>
  (dispatch: (action: SelectionAction) => void): Promise<void> => {
    let spaceGroupIds: number[] = [];
    let spaceIds: number[] = [];
    let memberTagIds: number[] = [];
    let communityMemberIds: number[] = [];
    if (value && "space_groups_ids" in value) {
      spaceGroupIds = value.space_groups_ids || [];
      spaceIds = value.spaces_ids || [];
      memberTagIds = value.member_tags_ids || [];
      communityMemberIds = value.members_ids || [];
    } else if (!includeAllResultTypes && value) {
      communityMemberIds = value;
    }

    dispatch({
      type: SELECTION_ACTIONS.INITIAL_FETCH_START,
      payload: {
        spaceGroupIds,
        spaceIds,
        memberTagIds,
        communityMemberIds,
      },
    } as InitialFetchStartAction);

    return Promise.all([
      fetchSpaceGroups(spaceGroupIds, includeAllResultTypes)(dispatch),
      fetchSpaces(spaceIds, includeAllResultTypes)(dispatch),
      fetchMemberTags(memberTagIds, includeAllResultTypes)(dispatch),
      fetchCommunityMembers(
        communityMemberIds,
        includeAllResultTypes,
      )(dispatch),
    ])
      .then(() => {
        dispatch({
          type: SELECTION_ACTIONS.INITIAL_FETCH_SUCCESS,
          payload: {
            spaceGroupIds,
            spaceIds,
            memberTagIds,
            communityMemberIds,
          },
        } as InitialFetchSuccessAction);
      })
      .catch((err: Error) => {
        console.error(err);
        dispatch({
          type: SELECTION_ACTIONS.INITIAL_FETCH_FAILURE,
          payload: {
            spaceGroupIds,
            spaceIds,
            memberTagIds,
            communityMemberIds,
            errorMessage: err?.message || "Unknown error",
          },
        } as InitialFetchFailureAction);
      });
  };
